import { apiService } from './api.js';

/**
 * API Keys Service
 * Handles all API key related operations
 */
class ApiKeysService {
  /**
   * Create a new API key
   * @param {string} projectId - Project ID
   * @param {string} name - API key name
   * @param {string} description - API key description (optional)
   * @returns {Promise<Object>} Created API key data
   */
  async createApiKey(projectId, name, description = '') {
    try {
      const response = await apiService.post('/api-keys', {
        projectId,
        name,
        description,
      });
      return response;
    } catch (error) {
      console.error('Error creating API key:', error);
      throw error;
    }
  }

  /**
   * Get API keys with pagination
   * @param {string} projectId - Project ID
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 10)
   * @param {boolean} isActive - Filter by active status (optional)
   * @returns {Promise<Object>} API keys data with pagination
   */
  async getApiKeys(projectId, page = 1, limit = 10, isActive = null) {
    try {
      const params = new URLSearchParams({
        projectId,
        page: page.toString(),
        limit: limit.toString(),
      });

      if (isActive !== null) {
        params.append('isActive', isActive.toString());
      }

      const response = await apiService.get(`/api-keys?${params}`);
      return response;
    } catch (error) {
      console.error('Error fetching API keys:', error);
      throw error;
    }
  }

  /**
   * Get API key by ID
   * @param {string} apiKeyId - API key ID
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} API key data
   */
  async getApiKeyById(apiKeyId, projectId) {
    try {
      const params = new URLSearchParams({ projectId });
      const response = await apiService.get(`/api-keys/${apiKeyId}?${params}`);
      return response;
    } catch (error) {
      console.error('Error fetching API key:', error);
      throw error;
    }
  }

  /**
   * Update API key
   * @param {string} apiKeyId - API key ID
   * @param {string} projectId - Project ID
   * @param {string} name - New API key name
   * @param {string} description - New API key description (optional)
   * @returns {Promise<Object>} Updated API key data
   */
  async updateApiKey(apiKeyId, projectId, name, description = '') {
    try {
      const url = `/api-keys/${apiKeyId}`;

      const response = await apiService.put(url, {
        projectId,
        apiKeyId,
        name,
        description,
      });
      return response;
    } catch (error) {
      console.error('Error updating API key:', error);
      throw error;
    }
  }

  /**
   * Delete API key
   * @param {string} apiKeyId - API key ID
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteApiKey(apiKeyId, projectId) {
    try {
      const params = new URLSearchParams({
        projectId,
        apiKeyId,
      });

      const url = `/api-keys/${apiKeyId}?${params}`;

      const response = await apiService.delete(url);
      return response;
    } catch (error) {
      console.error('Error deleting API key:', error);
      throw error;
    }
  }

  /**
   * Regenerate API key
   * @param {string} apiKeyId - API key ID
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Regenerated API key data
   */
  async regenerateApiKey(apiKeyId, projectId) {
    try {
      const url = `/api-keys/${apiKeyId}/regenerate`;

      const response = await apiService.post(url, {
        projectId,
        apiKeyId,
      });
      return response;
    } catch (error) {
      console.error('Error regenerating API key:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const apiKeysService = new ApiKeysService();
